import { useEffect, useRef, useState } from 'react'
import { Clock } from 'lucide-react'
import { fraicheur } from '../shell/status.js'
import { nextNumberState, type NumberState } from './chart-scale.js'
import type { ChampsRattachement } from './rattachement-model.js'

/**
 * SOIRÉES — types API + utilitaires partagés (index, détail, carte Sécurité,
 * carte Dashboard). Heures TOUJOURS en Europe/Paris — le fuseau du club,
 * jamais celui du navigateur.
 */

/** Une soirée telle que les listes la reçoivent — avec ses événements rattachés (19/10/2026). */
export interface SoireeView extends ChampsRattachement {
  id: string
  name: string
  family: string | null
  starts_at: string
  ends_at: string | null
  phase: 'UPCOMING' | 'ONGOING' | 'FINISHED' | 'CANCELLED'
  phase_label: string
  source: string
  tickets_scanned: number | null
  tickets_valid: number | null
  scan_rate: number | null
  /** Quand cet état des ventes a été observé — les ventes ne sont pas
   *  temps réel, et une donnée périmée ne doit pas passer pour la vérité. */
  tickets_observed_at: string | null
  /** « J-1 », « ce soir » — décompte en NUITS commerciales, calculé à la
   *  lecture. À ne pas confondre avec le jalon de prévision. */
  countdown_label: string | null
  /** Événements d'associations d'une même nuit regroupés en une ligne (« INTERSANTE (×9) »). */
  groupe?: { nombre: number; ids: string[]; noms: string[]; nuit: string } | null
  /**
   * La soirée en cours seulement : un événement rattaché a ouvert la porte
   * avant elle — « Commence à 23 h 55 · MEDECINE déjà ouverte ». Sa phase est
   * alors « en cours » (la nuit a commencé).
   */
  ouverte_avant?: { commence_a: string; ouverts: string[]; libelle: string } | null
}

export interface TicketingFreshness {
  state: 'HEALTHY' | 'DEGRADED' | 'UNAVAILABLE' | 'STALE' | 'UNKNOWN'
  seconds_since_success: number | null
  stale: boolean
  configured: boolean
  provider: string | null
  last_error: string | null
}

export interface SoireeStats {
  tickets_issued: number
  tickets_valid: number
  tickets_scanned: number
  tickets_refunded: number
  tickets_cancelled: number
  tickets_resold: number
  tickets_invitation: number
}

export const SOURCE_LABELS: Record<string, string> = {
  shotgun: 'Shotgun',
  // Les soirées d'associations (BDE), vendues sur Pretix (16/10/2026).
  pretix: 'Pretix (associations)',
  internal: 'Billetterie Live Club',
  manual: 'Sans billetterie',
}

const ZONE = { timeZone: 'Europe/Paris' } as const

/** « Samedi 22 août » (Europe/Paris). */
export function jourFr(iso: string): string {
  const s = new Date(iso).toLocaleDateString('fr-FR', {
    weekday: 'long', day: 'numeric', month: 'long', ...ZONE,
  })
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/** « Samedi 22 août 2026 ». */
export function jourFrAnnee(iso: string): string {
  const s = new Date(iso).toLocaleDateString('fr-FR', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', ...ZONE,
  })
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/** « 23:30 » (Europe/Paris). */
export function heureFr(iso: string): string {
  return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', ...ZONE })
}

/** « 23:30 → 06:00 » — ou « 23:30 » seul si la fin est inconnue. */
export function plageHoraire(startsAt: string, endsAt: string | null): string {
  return endsAt ? `${heureFr(startsAt)} → ${heureFr(endsAt)}` : heureFr(startsAt)
}

/** Pastille de phase — les couleurs suivent la grammaire LIVE CONTROL. */
export function PhasePill({ phase, label }: { phase: SoireeView['phase']; label: string }) {
  if (phase === 'ONGOING') {
    return <span className="lc-pill lc-pill--ok"><span className="lc-dot lc-dot--ok lc-dot--live" />{label}</span>
  }
  if (phase === 'CANCELLED') return <span className="lc-pill lc-pill--error">{label}</span>
  if (phase === 'UPCOMING') return <span className="lc-pill lc-pill--idle">{label}</span>
  return <span className="lc-pill lc-pill--idle">{label}</span>
}

/**
 * Ligne de fraîcheur HONNÊTE : « Dernière synchronisation : il y a 8 s »,
 * et — panne fournisseur — « Dernières données fiables il y a 4 min ·
 * Source billetterie indisponible ». Jamais un 0 fabriqué.
 */
export function FreshnessLine({ f }: { f: TicketingFreshness | null }) {
  if (!f) return null
  if (!f.configured) {
    return <p className="so-fresh so-fresh--warn"><Clock size={13} aria-hidden="true" />Billetterie non configurée</p>
  }
  if (f.state === 'UNKNOWN') {
    return <p className="so-fresh"><Clock size={13} aria-hidden="true" />Première synchronisation en attente</p>
  }
  const age = fraicheur(f.seconds_since_success, f.stale) ?? 'jamais synchronisé'
  if (f.state === 'UNAVAILABLE') {
    return (
      <p className="so-fresh so-fresh--warn">
        <Clock size={13} aria-hidden="true" />
        {age} · Source billetterie indisponible — Gestion réessaie automatiquement
      </p>
    )
  }
  if (f.stale) {
    return <p className="so-fresh so-fresh--warn"><Clock size={13} aria-hidden="true" />{age}</p>
  }
  return <p className="so-fresh"><Clock size={13} aria-hidden="true" />Dernière synchronisation : {age.replace(/^Actualisé /, '')}</p>
}

/* ═══════════ projection opérationnelle (fraîcheur PAR source) ═══════════ */

export type SourceQuality = 'LIVE' | 'STALE' | 'UNAVAILABLE' | 'UNKNOWN' | 'NOT_YET_AVAILABLE' | 'FINAL'

export interface SourceState { quality: SourceQuality; seconds_since_success: number | null }

/** Provenances possibles d'une programmation — miroir du type serveur. */
export type ProgrammingSource = 'MANUAL' | 'SHOTGUN_TITLE' | 'MAIN_COURANTE'

export interface ProgrammedDj {
  key: string
  name: string
  sources: ProgrammingSource[]
  entry_id?: string
}

/** Une salle physique, toutes sources réunies. Composée par le serveur. */
export interface RoomProgramming {
  code: string | null
  label: string
  djs: ProgrammedDj[]
  room_sources: ProgrammingSource[]
  conflict: boolean
}

export interface SoireeProjection {
  generated_at: string
  ticketing: TicketingFreshness
  security: SourceState & {
    agents_on_duty: number | null; open_incidents: number | null
    critical_incidents: number | null; entries_total: number | null; service_status: string | null
  }
  staff: SourceState & { present_confirmed: number | null; uncertain: number | null }
  infrastructure: SourceState & { sources_total: number | null; sources_attention: number | null; critical_alerts: number | null }
  access: SourceState & { status: string | null }
  lineup: {
    slots: { room_code: string | null; room_label: string | null; artist: string | null; confidence: string }[]
    rooms_open: number | null
    confidence: 'CONFIRMED' | 'LIKELY' | 'UNKNOWN'
  } | null
  /** Les salles telles qu'elles doivent s'afficher — composées par le serveur,
   *  une entrée par salle physique, toutes provenances réunies. */
  room_programming: RoomProgramming[]
  markers: { type: string; occurred_at: string; recorded_by_name: string | null }[]
  /** Annonce de fermeture RÉSOLUE — miroir de la primitive serveur. */
  closing_announcement: {
    state: 'RECORDED' | 'PLANNED' | 'NONE' | 'UNKNOWN'
    at: string | null
    recorded_by_name: string | null
    source: 'GESTION' | 'MAIN_COURANTE' | null
  }
  /** LIVE pendant la soirée, FINAL une fois terminée (§11-12). */
  mode: 'LIVE' | 'FINAL'
  operational_summary: {
    security_agents_peak: number | null; security_agents_total: number | null
    security_agents_carded: number | null
    incidents_total: number | null; critical_incidents_total: number | null
    closing_announcement_at: string | null
    opened_at: string | null; closed_at: string | null; computed_at: string
  } | null
  source_coverage: { source: string; label: string; state: string; message: string | null }[]
  main_courante_link: { external_id: string; match_method: string; confidence: string
    reason: string | null; confirmed: boolean } | null
  can_drill: { security: boolean; presence: boolean; infrastructure: boolean }
}

/** Pastille de qualité de source : ● LIVE 8 s · ⚠ STALE 2 min · UNKNOWN. */
export function QualityDot({ s }: { s: SourceState }) {
  const age = s.seconds_since_success
  const ageTxt = age === null ? '' : age < 120 ? `${age} s` : age < 7200 ? `${Math.round(age / 60)} min` : `${Math.round(age / 3600)} h`
  if (s.quality === 'LIVE') {
    return <span className="so-quality so-quality--live"><span className="lc-dot lc-dot--ok lc-dot--live" />LIVE · {ageTxt}</span>
  }
  if (s.quality === 'STALE') return <span className="so-quality so-quality--warn">⚠ STALE · {ageTxt}</span>
  if (s.quality === 'UNAVAILABLE') return <span className="so-quality so-quality--warn">⚠ Indisponible · {ageTxt || '—'}</span>
  // La source n'existait pas encore ce soir-là : ce n'est ni un silence, ni
  // un zéro — c'est une absence d'outil, et on le dit.
  if (s.quality === 'NOT_YET_AVAILABLE') {
    return <span className="so-quality so-quality--idle">Pas encore en service</span>
  }
  // Soirée terminée : la donnée ne « vieillit » plus, elle est définitive.
  if (s.quality === 'FINAL') return <span className="so-quality so-quality--idle">Donnée finale</span>
  return <span className="so-quality so-quality--idle">Aucune donnée</span>
}

/**
 * Valeur numérique HONNÊTE : null (source muette) → « — », jamais 0.
 * 0 signifie « zéro, avec certitude ».
 */
export function honnete(v: number | null | undefined): string {
  return v === null || v === undefined ? '—' : String(v)
}

/** « T+2h15 » — temps écoulé depuis le début, la référence des nuits. */
export function tPlus(startsAt: string, at = Date.now()): string {
  const s = Math.max(0, Math.floor((at - new Date(startsAt).getTime()) / 1000))
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60)
  return `T+${h}h${String(m).padStart(2, '0')}`
}

/** « 3 h 09 » — durée humaine. */
export function dureeFr(s: number): string {
  const h = Math.floor(s / 3600), m = Math.round((s % 3600) / 60)
  return h > 0 ? `${h} h ${String(m).padStart(2, '0')}` : `${m} min`
}

/* Le respect de `prefers-reduced-motion` a quitté le JavaScript : il vit
   désormais en CSS, là où le navigateur l'applique sans qu'on ait à le
   demander — et où il continue de fonctionner si le réglage change en
   cours de session. */
/**
 * LE GRAND NOMBRE VIVANT.
 *
 * Trois refus, qui viennent de ce que ce chiffre SIGNIFIE :
 *
 *  · PAS DE COMPTAGE DEPUIS ZÉRO au chargement. Faire défiler 0 → 81 est
 *    spectaculaire et faux : personne n'a observé 37 billets scannés.
 *  · PAS DE VALEURS INTERMÉDIAIRES lors d'un changement. Une synchronisation
 *    qui passe de 81 à 89 n'a jamais constaté 82, 83, 84. L'ancienne
 *    interpolation les affichait toutes ; le nouveau chiffre remplace
 *    directement l'ancien.
 *  · PAS DE COULEUR « SUCCÈS » sur une variation. Les scans montent en
 *    général, mais une correction de source peut les faire redescendre — et
 *    ce n'est pas un échec. Le sens du mouvement se lit, il ne se juge pas.
 *
 * L'animation ne se déclenche que sur une valeur RÉELLEMENT nouvelle : un
 * rendu React, un retour d'onglet ou un changement de mesure ailleurs sur la
 * page ne la réveillent pas.
 */
export function LiveNumber({ value, format = (n) => String(n), className }: {
  value: number | null
  format?: (n: number) => string
  className?: string
}) {
  /* `seq` force le remontage des deux couches : c'est lui qui rejoue
     l'animation, et rien d'autre. */
  const [etat, setEtat] = useState<NumberState>(() => ({ prev: null, cur: value, dir: 0, seq: 0 }))
  // La règle de transition vit dans un module pur — elle est testée là-bas,
  // et ce composant ne fait que la jouer.
  useEffect(() => { setEtat((e) => nextNumberState(e, value)) }, [value])

  const { prev, cur, dir, seq } = etat
  if (cur === null) return <span className={className}>—</span>

  return (
    <span className={`lc-num${className ? ` ${className}` : ''}`} data-dir={dir}>
      {/* L'ancienne valeur quitte l'écran SANS occuper de place : sans
          position absolue, la carte sautait d'une ligne le temps du relais. */}
      {prev !== null ? (
        <span
          className="lc-num__out" key={`o${seq}`} aria-hidden="true"
          /*
           * Une fois sa sortie terminée, la couche QUITTE le DOM. Se contenter
           * de l'y laisser à opacité nulle suffit tant que rien ne réécrit son
           * animation — et c'est précisément ce qui est arrivé. Au repos, il
           * n'existe désormais qu'une seule couche de texte, par construction.
           *
           * Le garde sur `seq` évite qu'une sortie tardive n'efface la couche
           * d'une transition PLUS RÉCENTE, quand deux synchronisations se
           * suivent de près.
           */
          onAnimationEnd={(e) => {
            if (e.animationName !== 'lc-num-out') return
            setEtat((s) => (s.seq === seq ? { ...s, prev: null } : s))
          }}
        >{format(prev)}</span>
      ) : null}
      <span className="lc-num__in" key={`i${seq}`}>{format(cur)}</span>
    </span>
  )
}

/**
 * Variante DISCRÈTE pour les petits indicateurs (§31) : la valeur change en
 * fondu, sans translation. Le regard doit rester sur le grand nombre — une
 * carte où tout bouge en même temps ne dit plus où regarder.
 */
export function QuietNumber({ value, format = (n) => String(n) }: {
  value: number | null
  format?: (n: number) => string
}) {
  const [seq, setSeq] = useState(0)
  const dernier = useRef<number | null>(value)
  useEffect(() => {
    if (dernier.current === value) return
    dernier.current = value
    setSeq((x) => x + 1)
  }, [value])
  if (value === null) return <>—</>
  return <span className="lc-quiet" key={seq}>{format(value)}</span>
}

/** Aide terminologique — obligatoire partout où le compteur apparaît. */
export const AIDE_SCANS =
  'Les sorties ne sont pas comptabilisées. Ce chiffre correspond aux billets '
  + 'enregistrés à l’entrée et non au nombre de personnes actuellement présentes.'

/* ═══════════════════════════ MAINTENANT ═══════════════════════════ */

/**
 * L'HORLOGE DU PRODUIT — une seule, partagée.
 *
 * La Timeline avait la sienne ; Analytics allait s'en fabriquer une seconde.
 * Deux `setInterval` sur la même page auraient fini par désigner deux
 * « maintenant » légèrement différents sur deux représentations du même axe.
 *
 * La minute suffit : sur une nuit de 7 h, une minute vaut 0,24 % de l'axe —
 * moins d'un pixel sur la plupart des écrans. Aucune boucle à 60 FPS.
 */
export function useNowMinute(actif = true): number {
  const [ms, setMs] = useState(() => Date.now())
  useEffect(() => {
    if (!actif) return
    setMs(Date.now())
    const t = setInterval(() => setMs(Date.now()), 60_000)
    return () => clearInterval(t)
  }, [actif])
  return ms
}

/**
 * LE REPÈRE « MAINTENANT » — une seule définition visuelle.
 *
 * Fine verticale, tête lumineuse qui respire, libellé compact. La Timeline
 * l'utilise à pleine intensité (`timeline`), Analytics en retrait (`chart`) :
 * là-bas le repère accompagne la lecture d'une courbe, il ne doit pas
 * concurrencer le tracé.
 *
 * `frac` est une fraction de l'axe RÉELLEMENT affiché, calculée par
 * l'appelant à partir de ses propres bornes — jamais de la largeur du
 * composant. Hors bornes, l'appelant passe `null` : un repère collé au bord
 * dirait « maintenant est ici » alors qu'il est ailleurs.
 */
export function NowMarker({ frac, variant = 'timeline', label = 'Maintenant' }: {
  frac: number | null
  variant?: 'timeline' | 'chart'
  label?: string
}) {
  if (frac === null) return null
  return (
    <div
      className={`so-now so-now--${variant}`}
      style={{ left: `${frac * 100}%` }}
      /* Passé 78 % de l'axe, le libellé repart vers la gauche : à droite il
         sortirait de la carte (§32). */
      data-side={frac > 0.78 ? 'left' : 'right'}
    >
      <span className="so-now__head" aria-hidden="true" />
      <span className="so-now__label">{label}</span>
    </div>
  )
}
